import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo, Ota } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Period, Ratesummary } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type MealTypeValues,
  type MembershipType,
  RATE_NO_VALUE,
} from 'lumen/utils/constants/general-constants';
import type { OpenRoomPricingFixedCategory } from 'lumen/utils/constants/pricing-assistant-constants';

export interface ComparisonRate {
  best_flex: boolean;
  currency: string;
  difference_absolute: number;
  difference_relative: number;
  mealtype_included: number;
  message: string | null;
  room_name: string;
  value: number;
  vat_incl: boolean;
}

export default class Rate extends Model {
  [Type] = 'rate' as const;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('ota', { async: false, inverse: null })
  declare ota: Ota;

  @belongsTo('period', { async: false, inverse: 'ratesModel' })
  declare period: Period;

  @attr('boolean') declare bestFlex: boolean;
  @attr('boolean') declare cancellable: boolean;
  @attr('string') declare cemaCategory: OpenRoomPricingFixedCategory | '';
  @attr('number') declare cityTax: number;
  @attr('number') declare cityTaxIncl: number;
  @attr() declare comparisonRates: ComparisonRate[] | undefined;
  @attr('string') declare currency: SupportedCurrencies;
  @attr('number') declare differenceWithBaserate: number;
  @attr('dayjs') declare extractDatetime: DayjsTransformReturn;
  @attr('boolean') declare isBaserate: boolean;
  @attr('boolean') declare isOutOfSync: boolean;
  @attr('number') declare maxPersons: number;
  @attr('number') declare mealtypeIncluded: MealTypeValues;
  @attr('number') declare membershiptype: MembershipType;
  @attr('string') declare message: string;
  @attr('number') declare otherTaxes: number;
  @attr('boolean') declare otherTaxesIncl: boolean;
  @attr('number') declare positionToBaserate: number;
  @attr() declare rateChanges: unknown;
  @attr('string') declare roomName: string;
  @attr('string') declare roomType: string;
  @attr('string') declare source: string;
  @attr('number') declare value: number;
  @attr('number') declare vat: number;
  @attr('boolean') declare vatIncl: boolean;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @tracked ratesummary: Ratesummary | null = null;

  modelName = 'rate';

  get formattedPriceChangeSinceYesterday(): string | null {
    return this.hotelAndMarketService.formatRate(
      this.priceChangeSinceYesterday,
      true,
      false, // no long format
      undefined,
      true, // include sign
    );
  }

  get formattedRate(): string {
    const formattedRate = this.hotelAndMarketService.formatRate(
      this,
      true,
      false,
    );

    if (!formattedRate) {
      return this.intl.t('general.missing');
    }

    return formattedRate;
  }

  get hasValue(): boolean {
    return this.value !== RATE_NO_VALUE;
  }

  get isBestFlexRate(): boolean {
    return this.bestFlex;
  }

  get isLowestFlexRate(): boolean {
    return !this.isBestFlexRate && this.cancellable;
  }

  get priceChangeSinceYesterday(): number | null {
    if (
      this.hasValue &&
      this.comparisonRates?.[0] &&
      Math.abs(this.comparisonRates[0].difference_absolute) >= 1 // avoid showing +/-0 in the UI
    ) {
      return this.comparisonRates[0].difference_absolute;
    }

    return null;
  }

  get type(): 'rate' {
    return this[Type];
  }

  // To log the rate object in the console, we need to use the toJSON method, otherwise we get the proxy object
  toJSON(): unknown {
    return this.serialize();
  }
}
